import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import StyledTableCell from "../../layouts/table/StyledTableCell";
import StyledTableRow from "../../layouts/table/StyledTableRow";
import { Card, CardContent } from "@mui/material";
import Typography from "@mui/material/Typography";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import esLocale from "date-fns/locale/es";
import { parseISO, format as formatDate } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ScaleIcon from '@mui/icons-material/Scale';
import DeleteItemModal from "../DeleteItemModal";
import { toast } from "react-toastify";
import InvoiceService from "../../../services/invoice.service";
import InvoiceModal from "./InvoiceModal";
import useRole from "../../../hooks/useRole";
import Roles from "../../../helpers/roles";
import InvoiceModalPDF from "./InvoiceModalPDF";
import ArticleIcon from '@mui/icons-material/Article';
import { convertDate2 } from "../../../helpers/utils";
import UpdateInvoiceWeight from "./UpdateInvoiceWeight";
import InvoiceUpdateModal from "./InvoiceUpdateModal";
import InvoiceStates from "../../../helpers/InvoiceStates";


const todayString = () => formatDate(new Date(), 'yyyy-MM-dd');

const initialInvoice = {
    id: '',
    invoiceNumber: '',
    destination: '',
    state: '',
    entryTime: '00:00',
    departureTime: '00:00',
    customerId: '',
    createdAt: todayString(),
    zoneId: '',
    driverId: '',
    dispatcherId: '',
    vehicleId: '',
    freight: '',
    weight: 0,
    woodType: '',
    order: '',
    delivery: '',
    bill: '',
    safeConduct: '',
    references: [],
}

const STATE_OPTIONS = [
    InvoiceStates.new,
    InvoiceStates.copy,
    InvoiceStates.modified,
    InvoiceStates.corrected,
    InvoiceStates.printed,
    InvoiceStates.settled,
];

const InvoiceList = () => {

    const topRef = useRef(null);
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(initialInvoice)
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { role } = useRole();

    // pagination
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);

    // filters
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    //modals
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [openCreateInvoiceModal, setOpenCreateInvoiceModal] = useState(false);
    const [openEditInvoiceModal, setOpenEditInvoiceModal] = useState(false);
    const [openDeleteInvoiceModal, setOpenDeleteInvoiceModal] = useState(false);
    const [openUpdateInvoiceWeight, setOpenUpdateInvoiceWeight] = useState(false)

    // Debounce search input → search (300 ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Fetch invoices when pagination or filters change. Skip while a modal is open.
    useEffect(() => {
        if (openCreateInvoiceModal || openEditInvoiceModal || openDeleteInvoiceModal || openInvoiceModal) {
            return;
        }
        const controller = new AbortController();
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const params = {
                    page: page + 1,
                    limit,
                    sortBy: 'createdAt',
                    sortOrder: 'desc',
                };
                if (search) params.search = search;
                if (stateFilter) params.state = stateFilter;
                if (dateFrom) params.dateFrom = dateFrom;
                if (dateTo) params.dateTo = dateTo;

                const result = await InvoiceService.getInvoicesPaginated(params, controller.signal);
                setInvoices(Array.isArray(result?.data) ? result.data : []);
                setTotal(result?.pagination?.total ?? 0);
            } catch (e) {
                if (e.name === 'AbortError') return;
                setError(e.message);
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
        return () => controller.abort();
    }, [page, limit, search, stateFilter, dateFrom, dateTo,
        openCreateInvoiceModal, openEditInvoiceModal, openDeleteInvoiceModal, openInvoiceModal]);

    const removeInvoiceHandler = async () => {
        try {
            await InvoiceService.removeInvoice(selectedInvoice.id);
            toast.success('Se ha eliminado correctamente');
            toggleModal('remove', false)
        } catch (e) {
            console.error(e);
            toast.error('There was an error trying to remove this invoice');
        }
    };

    const openCreateInvoiceHandler = () => {
        setSelectedInvoice(() => initialInvoice)
        toggleModal('create', true);
    }

    const openInvoiceModalHandler = (invoice) => {
        setSelectedInvoice(() => invoice)
        toggleModal('view', true);
    }

    const openEditInvoiceHandler = (invoice) => {
        setSelectedInvoice(() => invoice)
        toggleModal('edit', true);
    }

    const openRemoveInvoiceHandler = (invoice) => {
        setSelectedInvoice(invoice)
        toggleModal('remove', true);
    }

    const openUpdateInvoiceWeightHandler = (invoice) => {
        setSelectedInvoice(() => invoice)
        setOpenUpdateInvoiceWeight(true)
    }

    const toggleModal = (modalType, open) => {
        switch (modalType) {
            case 'create':
                setOpenCreateInvoiceModal(open);
                break;
            case 'edit':
                setOpenEditInvoiceModal(open);
                break;
            case 'remove':
                setOpenDeleteInvoiceModal(open);
                break;
            case 'view':
                setOpenInvoiceModal(open)
                break;
            default:
                console.error(`Modal type ${modalType} is not valid`);
        }
    }

    const handlePaginationChange = (event, newPage) => setPage(newPage - 1);

    const shouldScrollAfterFetch = useRef(false);

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
        shouldScrollAfterFetch.current = true;
    };

    // Scroll to top once the fetch triggered by limit change completes
    useEffect(() => {
        if (!isLoading && shouldScrollAfterFetch.current) {
            shouldScrollAfterFetch.current = false;
            if (topRef.current) {
                topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }, [isLoading]);

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const fromRow = total === 0 ? 0 : page * limit + 1;
    const toRow = Math.min((page + 1) * limit, total);

    const resetPageAndSet = (setter) => (e) => {
        setter(e.target.value);
        setPage(0);
    };

    const clearFilters = () => {
        setSearchInput("");
        setSearch("");
        setStateFilter("");
        setDateFrom("");
        setDateTo("");
        setPage(0);
    };

    const hasActiveFilters = searchInput || stateFilter || dateFrom || dateTo;

    return (
        <>
            <div ref={topRef} />
            <Card sx={{ overflow: 'hidden' }}>
                <CardContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                        {/* Single row: title + filters + actions */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant='h4' sx={{ flexShrink: 0, mr: 1 }}>
                                Remisiones
                            </Typography>
                            <TextField
                                label="Buscar (# remisión, cliente o destino)"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                size="small"
                                sx={{ flex: '1 1 220px', minWidth: 200 }}
                            />
                            <TextField
                                select
                                label="Estado"
                                value={stateFilter}
                                onChange={resetPageAndSet(setStateFilter)}
                                size="small"
                                sx={{ minWidth: 140 }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {STATE_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </TextField>
                            <DatePicker
                                label="Desde"
                                value={dateFrom ? parseISO(dateFrom) : null}
                                onChange={(newValue) => {
                                    setDateFrom(newValue ? formatDate(newValue, 'yyyy-MM-dd') : '');
                                    setPage(0);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" sx={{ width: 170 }} />
                                )}
                                inputFormat="yyyy-MM-dd"
                            />
                            <DatePicker
                                label="Hasta"
                                value={dateTo ? parseISO(dateTo) : null}
                                onChange={(newValue) => {
                                    setDateTo(newValue ? formatDate(newValue, 'yyyy-MM-dd') : '');
                                    setPage(0);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" sx={{ width: 170 }} />
                                )}
                                inputFormat="yyyy-MM-dd"
                            />
                            {hasActiveFilters && (
                                <Button
                                    variant="contained"
                                    onClick={clearFilters}
                                    sx={{ height: 40 }}
                                >
                                    Limpiar filtros
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<ControlPointIcon />}
                                onClick={openCreateInvoiceHandler}
                                sx={{ height: 40, whiteSpace: 'nowrap' }}
                            >
                                Añadir nuevo
                            </Button>
                        </Box>
                    </LocalizationProvider>

                    {isLoading && <p>Cargando remisiones...</p>}
                    {!isLoading && !error && (!Array.isArray(invoices) || invoices.length === 0) && (
                        <p>{hasActiveFilters ? 'No se encontraron remisiones con esos filtros.' : 'No se han creado remisiones.'}</p>
                    )}
                    {error && <p> {error} </p>}
                    {Array.isArray(invoices) && invoices.length > 0 &&
                        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="small" sx={{
                                minWidth: 600,
                                '& .MuiTableCell-root': { py: 0.75 },
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>N. Remisión</StyledTableCell>
                                        <StyledTableCell>Cliente</StyledTableCell>
                                        <StyledTableCell>Destino</StyledTableCell>
                                        <StyledTableCell>Zona Finca</StyledTableCell>
                                        <StyledTableCell>Estado</StyledTableCell>
                                        <StyledTableCell>Fecha</StyledTableCell>
                                        <StyledTableCell align="center">Acciones</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((invoice) => (
                                        <StyledTableRow
                                            key={invoice.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                {invoice.invoiceNumber}
                                            </TableCell>
                                            <TableCell>{invoice.customer?.name}</TableCell>
                                            <TableCell>{invoice.destination}</TableCell>
                                            <TableCell>{invoice.zone?.name}</TableCell>
                                            <TableCell>{invoice.state}</TableCell>
                                            <TableCell>{convertDate2(invoice.createdAt)}</TableCell>
                                            <TableCell align="right" sx={{ py: 0, whiteSpace: 'nowrap' }}>
                                                <Button size="small" onClick={() => openInvoiceModalHandler(invoice)} sx={{ minWidth: 36, p: 0.5 }}>
                                                    <ArticleIcon fontSize="small" />
                                                </Button>
                                                {
                                                    invoice.state !== 'Liquidado' && <>
                                                        <Button size="small" onClick={() => openEditInvoiceHandler(invoice)} sx={{ minWidth: 36, p: 0.5 }}>
                                                            <EditIcon fontSize="small" />
                                                        </Button>
                                                    </>
                                                }
                                                {
                                                    invoice.state !== 'Liquidado' && <>
                                                        <Button size="small" onClick={() => openUpdateInvoiceWeightHandler(invoice)} sx={{ minWidth: 36, p: 0.5 }}>
                                                            <ScaleIcon fontSize="small" color={'success'} />
                                                        </Button>
                                                    </>
                                                }
                                                {role === Roles.Admin &&
                                                    <>
                                                        {
                                                            invoice.state !== 'Liquidado' && <>
                                                                <Button size="small" onClick={() => openRemoveInvoiceHandler(invoice)} sx={{ minWidth: 36, p: 0.5 }}>
                                                                    <DeleteIcon fontSize="small" color={'warning'} />
                                                                </Button>
                                                            </>
                                                        }
                                                    </>
                                                }
                                            </TableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                px: 3,
                                py: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) => theme.palette.grey[50],
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Filas por página:
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={limit}
                                        onChange={handleLimitChange}
                                        sx={{
                                            minWidth: 80,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    >
                                        {[10, 20, 50, 100].map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>

                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={handlePaginationChange}
                                    color="primary"
                                    shape="rounded"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            margin: '0 2px',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: (theme) => `${theme.palette.primary.main}1A`,
                                            },
                                        },
                                        '& .MuiPaginationItem-root.Mui-selected': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}66`,
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            },
                                        },
                                        '& .MuiPaginationItem-ellipsis': {
                                            color: 'text.secondary',
                                        },
                                    }}
                                />

                                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 180, textAlign: 'right' }}>
                                    Mostrando <strong>{fromRow}-{toRow}</strong> de <strong>{total.toLocaleString('es-CO')}</strong>
                                </Typography>
                            </Box>
                        </TableContainer>}
                </CardContent>
            </Card>

            {/*Modals*/}
            <InvoiceModalPDF open={openInvoiceModal}
                invoiceId={selectedInvoice.id}
                onClose={() => toggleModal('view', false)}
            />
            <InvoiceModal open={openCreateInvoiceModal}
                modalType="CREATE"
                invoice={selectedInvoice}
                onClose={() => toggleModal('create', false)}
            />
            <InvoiceUpdateModal open={openEditInvoiceModal}
                modalType="EDIT"
                invoice={selectedInvoice}
                onClose={() => toggleModal('edit', false)}
            />
            <UpdateInvoiceWeight
                open={openUpdateInvoiceWeight}
                invoice={selectedInvoice}
                onClose={setOpenUpdateInvoiceWeight}
            />
            {role === Roles.Admin &&
                <>
                    <DeleteItemModal open={openDeleteInvoiceModal}
                        onClose={() => toggleModal('remove', false)}
                        onRemove={removeInvoiceHandler}
                        itemName={selectedInvoice.invoiceNumber}
                        item={"remisión"} />
                </>
            }
        </>

    )
}

export default InvoiceList;
